import { useCallback, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Ban, CircleSlash, Download, ExternalLink, HelpCircle, History, Pencil, RotateCcw, ShieldBan, ShieldCheck, Trash2, Undo2, X, ZapOff } from 'lucide-react'
import { ColumnDef, ColumnFiltersState } from '@tanstack/react-table'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import VirtualTable from '../components/VirtualTable'
import CorrectionWizard from '../components/CorrectionWizard'
import DeductibleHistoryModal from '../components/DeductibleHistoryModal'
import XLSX from 'xlsx-js-style'

// -- Data types matching backend MaterialsResponse --

interface InspectionRow {
  id: string
  bill_uuid: string | null
  booking_datetime: string | null
  is_combination: boolean
  booking_uuid: string | null
  user_uuid: string | null
  name_first: string | null
  name_last: string | null
  additional_info: string | null
  account_type: number | null
  account_type_name: string | null
  address: string | null
  copayment: string | null
  distance_meters: number | null
  distance_percentage: number | null
  customer_share: string | null
  items: { type: string; type_id: number; amount_net: string; distance_meters?: number }[] | null
  vehicle_car_id: string | null
  vehicle_reg_number: string | null
  cost_center: string | null
  municipality_name: string | null
  deductible_invoiced: boolean
  correction_comments: string | null
  deductible_corrections: {
    original_value: string
    corrected_value: string
    correction_type: string
    correction_id: string
    created_by_name: string
    created_at: string
    is_picked_up: boolean
  }[]
}

interface AccountingRow {
  id: string
  bill_uuid: string | null
  bill_datetime: string | null
  cost_center: string | null
  cost_center_description: string | null
  amount_net: string | null
  billing_group: string | null
  municipality_name: string | null
  booking_count: number
  sub_organization_uuid: string | null
  sub_org_prefix: string | null
  vehicle_car_id: string | null
  vehicle_reg_number: string | null
  is_blocked: boolean
  is_rejected: boolean
  is_non_billable: boolean
  invoice_line_id: string | null
  invoice_access_code: string | null
  invoice_status: string | null
  invoice_line_number: number | null
  entry_type: string
  is_credited: boolean
  original_entry_id: string | null
  correction_reason: string | null
  correction_comments: string | null
  correction_batch_id: string | null
  credited_at: string | null
  credited_by_name: string | null
  sequence_number: number | null
  subRows: InspectionRow[]
}

interface MaterialsResponse {
  data: AccountingRow[]
  accounting_count: number
  inspection_count: number
}

// We need a union type for TanStack Table since parent and child rows
// go through the same row model
type MaterialRow = AccountingRow & Partial<InspectionRow>

// -- Item type definitions for pivot columns (order matches business view) --
const ITEM_TYPES = [
  { id: 1, tKey: 'startFee' },
  { id: 3, tKey: 'wait' },
  { id: 8, tKey: 'waypoint' },
  { id: 4, tKey: 'routeDrive' },
  { id: 5, tKey: 'assistance' },
  { id: 6, tKey: 'stairclimber' },
  { id: 16, tKey: 'escort' },
  { id: 19, tKey: 'stretcher' },
] as const

// -- Formatters --

function formatDate(isoStr: string | null): string {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  return d.toLocaleDateString('fi-FI', { month: '2-digit', day: '2-digit' })
}

function formatTime(isoStr: string | null): string {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  return d.toLocaleTimeString('fi-FI', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatCurrency(val: string | null): string {
  if (!val) return ''
  return Number(val).toLocaleString('fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' \u20AC'
}

function formatPct(val: unknown): string {
  if (val == null || val === '') return ''
  const n = Number(val)
  return (n * 100).toFixed(1) + ' %'
}

function formatKm(meters: unknown): string {
  if (meters == null || meters === '') return ''
  const km = Number(meters) / 1000
  return km.toFixed(2) + ' km'
}

function truncUuid(val: unknown): string {
  if (!val) return ''
  const s = String(val)
  if (s.length < 12) return s
  return s.slice(0, 8) + '..'
}

export default function BillingMaterialsPage() {
  const { t } = useTranslation('billing')
  const { t: tc } = useTranslation('common')
  const { billExportUuid } = useParams<{ billExportUuid: string }>()

  const exportUuidParam =
    billExportUuid === '_open' ? '' : billExportUuid || ''

  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery<MaterialsResponse>({
    queryKey: ['billingMaterials', exportUuidParam],
    queryFn: () => {
      const params = new URLSearchParams()
      if (exportUuidParam) {
        params.set('bill_export_uuid', exportUuidParam)
      }
      return apiClient.get(`/billing/materials?${params.toString()}`)
    },
  })

  const [wizardBillUuid, setWizardBillUuid] = useState<string | null>(null)
  const [wizardPreInvoice, setWizardPreInvoice] = useState(false)
  const [mutationError, setMutationError] = useState<string | null>(null)

  // Shared error handler for all optimistic mutations
  function onMutationError(error: unknown) {
    const msg = error instanceof Error ? error.message : String(error)
    setMutationError(msg)
    setTimeout(() => setMutationError(null), 6000)
    // invalidateQueries in onSettled will restore correct state
  }

  // Helper: optimistically update parent rows in cache
  function updateParentInCache(predicate: (ae: AccountingRow) => boolean, updater: (ae: AccountingRow) => AccountingRow) {
    queryClient.setQueryData<MaterialsResponse>(['billingMaterials', exportUuidParam], (old) => {
      if (!old) return old
      return { ...old, data: old.data.map(ae => predicate(ae) ? updater(ae) : ae) }
    })
  }

  const blockMutation = useMutation({
    mutationFn: ({ billUuid, isBlocked }: { billUuid: string; isBlocked: boolean }) =>
      apiClient.patch(`/billing/bills/${billUuid}/block`, { is_blocked: isBlocked }),
    onMutate: ({ billUuid, isBlocked }) => {
      updateParentInCache(
        ae => ae.bill_uuid === billUuid,
        ae => ({ ...ae, is_blocked: isBlocked }),
      )
    },
    onError: onMutationError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['billingMaterials'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ billUuid, isRejected }: { billUuid: string; isRejected: boolean }) =>
      apiClient.patch(`/billing/bills/${billUuid}/reject`, { is_rejected: isRejected }),
    onMutate: ({ billUuid, isRejected }) => {
      updateParentInCache(
        ae => ae.bill_uuid === billUuid,
        ae => ({ ...ae, is_rejected: isRejected }),
      )
    },
    onError: onMutationError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['billingMaterials'] })
    },
  })

  const nonBillableMutation = useMutation({
    mutationFn: ({ billUuid, isNonBillable }: { billUuid: string; isNonBillable: boolean }) =>
      apiClient.patch(`/billing/bills/${billUuid}/non-billable`, { is_non_billable: isNonBillable }),
    onMutate: ({ billUuid, isNonBillable }) => {
      updateParentInCache(
        ae => ae.bill_uuid === billUuid,
        ae => ({ ...ae, is_non_billable: isNonBillable }),
      )
    },
    onError: onMutationError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['billingMaterials'] })
    },
  })

  const [deleteBatchId, setDeleteBatchId] = useState<string | null>(null)
  const [deleteSummary, setDeleteSummary] = useState<{
    credit_count: number; correction_count: number; inspection_count: number
    credit_total: string; correction_total: string
    entries: { entry_type: string; cost_center: string; cost_center_description: string; amount_net: string }[]
  } | null>(null)

  const deleteCorrectionMutation = useMutation({
    mutationFn: (batchId: string) =>
      apiClient.delete(`/billing/corrections/${batchId}`),
    onMutate: (batchId) => {
      // Optimistic: remove credit/correction rows and restore originals
      queryClient.setQueryData<MaterialsResponse>(['billingMaterials', exportUuidParam], (old) => {
        if (!old) return old
        return {
          ...old,
          data: old.data
            .filter(ae => ae.correction_batch_id !== batchId)
            .map(ae => ae.is_credited && ae.entry_type === 'original'
              ? { ...ae, is_credited: false }
              : ae
            ),
        }
      })
    },
    onSuccess: () => {
      setDeleteBatchId(null)
      setDeleteSummary(null)
    },
    onError: onMutationError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['billingMaterials'] })
    },
  })

  async function openDeleteConfirm(batchId: string) {
    try {
      const summary = await apiClient.get<typeof deleteSummary>(`/billing/corrections/${batchId}`)
      setDeleteSummary(summary)
      setDeleteBatchId(batchId)
    } catch {
      // If batch not found, ignore
    }
  }

  // Deductible correction state
  const [deductibleCorrModal, setDeductibleCorrModal] = useState<InspectionRow | null>(null)
  const [historyRow, setHistoryRow] = useState<InspectionRow | null>(null)
  const [deductibleSuccessMsg, setDeductibleSuccessMsg] = useState<string | null>(null)

  // Helper: optimistically replace a sub-row's deductible_corrections list in cache
  function setInspectionCorrectionsInCache(
    inspId: string,
    corrections: InspectionRow['deductible_corrections']
  ) {
    queryClient.setQueryData<MaterialsResponse>(['billingMaterials', exportUuidParam], (old) => {
      if (!old) return old
      return {
        ...old,
        data: old.data.map(ae => ({
          ...ae,
          subRows: ae.subRows.map(ie =>
            ie.id === inspId ? { ...ie, deductible_corrections: corrections } : ie
          ),
        })),
      }
    })
  }

  const deductibleZeroMutation = useMutation({
    mutationFn: (inspId: string) =>
      apiClient.post<{ result: string; original_value: string; correction_id: string }>('/billing/deductibles/corrections', {
        inspection_entry_id: inspId, action: 'zero',
      }),
    onMutate: (inspId) => {
      // Optimistic: find the row and append a pending correction immediately
      const old = queryClient.getQueryData<MaterialsResponse>(['billingMaterials', exportUuidParam])
      if (old) {
        for (const ae of old.data) {
          const ie = ae.subRows.find(r => r.id === inspId)
          if (ie) {
            setInspectionCorrectionsInCache(inspId, [
              ...ie.deductible_corrections,
              {
                original_value: ie.copayment || '0',
                corrected_value: '0',
                correction_type: 'direct',
                correction_id: '',
                created_by_name: '',
                created_at: new Date().toISOString(),
                is_picked_up: false,
              },
            ])
            break
          }
        }
      }
    },
    onSuccess: (data) => {
      // Server-truth refetch happens in onSettled; skip in-place patching here
      // since credit+correction paths may produce two rows per inspection entry.
      const msg = data.result.includes('credit')
        ? t('deductibleCorr.zeroCredit', { amount: data.original_value })
        : t('deductibleCorr.zeroDirect')
      setDeductibleSuccessMsg(msg)
      setTimeout(() => setDeductibleSuccessMsg(null), 5000)
    },
    onError: onMutationError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['billingMaterials'] })
    },
  })

  const deductibleCorrectMutation = useMutation({
    mutationFn: ({ inspId, value }: { inspId: string; value: number }) =>
      apiClient.post<{ result: string; original_value: string; corrected_value: string }>('/billing/deductibles/corrections', {
        inspection_entry_id: inspId, action: 'correct', corrected_value: value,
      }),
    onMutate: ({ inspId, value }) => {
      const old = queryClient.getQueryData<MaterialsResponse>(['billingMaterials', exportUuidParam])
      if (old) {
        for (const ae of old.data) {
          const ie = ae.subRows.find(r => r.id === inspId)
          if (ie) {
            setInspectionCorrectionsInCache(inspId, [
              ...ie.deductible_corrections,
              {
                original_value: ie.copayment || '0',
                corrected_value: String(value),
                correction_type: 'direct',
                correction_id: '',
                created_by_name: '',
                created_at: new Date().toISOString(),
                is_picked_up: false,
              },
            ])
            break
          }
        }
      }
    },
    onSuccess: (data) => {
      setDeductibleCorrModal(null)
      const msg = data.result.includes('credit')
        ? t('deductibleCorr.correctCredit', { from: data.original_value, to: data.corrected_value })
        : t('deductibleCorr.correctDirect', { from: data.original_value, to: data.corrected_value })
      setDeductibleSuccessMsg(msg)
      setTimeout(() => setDeductibleSuccessMsg(null), 5000)
    },
    onError: onMutationError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['billingMaterials'] })
    },
  })

  const deductibleUndoMutation = useMutation({
    mutationFn: (correctionId: string) =>
      apiClient.delete<{ original_value: string }>(`/billing/deductibles/corrections/${correctionId}`),
    onMutate: (correctionId) => {
      // Find and remove the matching correction optimistically
      const old = queryClient.getQueryData<MaterialsResponse>(['billingMaterials', exportUuidParam])
      if (old) {
        for (const ae of old.data) {
          const ie = ae.subRows.find(r =>
            r.deductible_corrections?.some(c => c.correction_id === correctionId)
          )
          if (ie) {
            setInspectionCorrectionsInCache(
              ie.id,
              ie.deductible_corrections.filter(c => c.correction_id !== correctionId)
            )
            break
          }
        }
      }
    },
    onSuccess: (data) => {
      setDeductibleSuccessMsg(t('deductibleCorr.undone', { amount: data.original_value }))
      setTimeout(() => setDeductibleSuccessMsg(null), 5000)
    },
    onError: onMutationError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['billingMaterials'] })
    },
  })

  // Define parent (accounting) columns
  const columns = useMemo<ColumnDef<MaterialRow, unknown>[]>(
    () => [
      {
        id: 'bill_datetime',
        accessorKey: 'bill_datetime',
        header: t('accounting.billDate'),
        cell: ({ getValue }) => formatDate(getValue() as string | null),
        filterFn: 'dateFilter',
        size: 55, minSize: 55,
      },
      {
        id: 'bill_uuid',
        accessorKey: 'bill_uuid',
        header: t('accounting.billUuid'),
        cell: ({ getValue }) => truncUuid(getValue()),
        filterFn: 'includesString',
        size: 95, minSize: 70,
      },
      {
        id: 'amount_net',
        accessorKey: 'amount_net',
        header: t('accounting.amountNet'),
        cell: ({ getValue }) => (
          <span className="font-mono">
            {formatCurrency(getValue() as string | null)}
          </span>
        ),
        filterFn: 'numericFilter',
        size: 85, minSize: 60,
      },
      {
        id: 'cost_center',
        accessorKey: 'cost_center',
        header: t('accounting.costCenter'),
        filterFn: 'includesString',
        size: 150, minSize: 60,
      },
      {
        id: 'cost_center_description',
        accessorKey: 'cost_center_description',
        header: t('accounting.description'),
        filterFn: 'includesString',
        size: 220, minSize: 80,
      },
      {
        id: 'inspection_count',
        accessorFn: (row: MaterialRow) => row.subRows?.length ?? 0,
        header: t('accounting.inspectionCount'),
        size: 60, minSize: 40,
      },
      {
        id: 'sub_org_prefix',
        accessorKey: 'sub_org_prefix',
        header: t('accounting.subOrgUuid'),
        filterFn: 'includesString',
        size: 100, minSize: 35,
      },
      {
        id: 'billing_group',
        accessorKey: 'billing_group',
        header: t('accounting.billingGroup'),
        filterFn: 'includesString',
        size: 550, minSize: 60,
      },
      {
        id: 'bill_status',
        accessorFn: (row: MaterialRow) => row.is_rejected ? 'H' : row.is_blocked ? 'E' : row.is_non_billable ? 'EL' : '',
        header: t('accounting.status'),
        cell: ({ row }) => {
          const r = row.original
          if (r.is_rejected) return <span className="text-red-700 font-semibold">H</span>
          if (r.is_blocked) return <span className="text-orange-600 font-semibold">E</span>
          if (r.is_non_billable) return <span className="text-blue-600 font-semibold" title={t('accounting.statusNonBillable')}>EL</span>
          return null
        },
        filterFn: 'includesString',
        size: 35, minSize: 30,
      },
      {
        id: 'invoice_ref',
        accessorFn: (row: MaterialRow) => row.invoice_access_code || '',
        header: t('materials.invoice'),
        cell: ({ row }) => {
          const r = row.original
          if (!r.invoice_access_code) return null
          const statusColor =
            r.invoice_status === 'published' ? 'text-green-700' :
            r.invoice_status === 'sent' ? 'text-blue-700' :
            'text-yellow-700'
          return (
            <span className={`text-xs font-mono ${statusColor}`}>
              {r.invoice_access_code}
            </span>
          )
        },
        filterFn: 'includesString',
        size: 80, minSize: 60,
      },
      {
        id: 'invoice_line_number',
        accessorFn: (row: MaterialRow) => row.invoice_line_number != null ? String(row.invoice_line_number) : '',
        header: t('materials.lineNumber'),
        cell: ({ row }) => {
          const r = row.original
          if (r.invoice_line_number == null) return null
          return <span className="text-xs font-mono">{r.invoice_line_number}</span>
        },
        filterFn: 'includesString',
        size: 80, minSize: 30,
      },
      {
        id: 'sequence_number',
        accessorFn: (row: MaterialRow) => row.sequence_number != null ? String(row.sequence_number) : '',
        header: t('materials.sequenceNumber'),
        cell: ({ row }) => {
          const r = row.original
          if (r.sequence_number == null) return null
          return <span className="text-xs font-mono text-gray-500" title={t('materials.sequenceNumberHelp')}>{r.sequence_number}</span>
        },
        filterFn: 'includesString',
        size: 50, minSize: 30,
      },
      {
        id: 'entry_type',
        accessorFn: (row: MaterialRow) => {
          if (row.entry_type === 'credit') return t('materials.credit')
          if (row.entry_type === 'correction') return t('materials.correction')
          if (row.entry_type === 'original_correction') return t('materials.originalCorrection')
          if (row.is_credited) return t('materials.credited')
          if ((row as MaterialRow & { is_corrected?: boolean }).is_corrected) return t('materials.corrected')
          return ''
        },
        header: t('materials.entryType'),
        cell: ({ row }) => {
          const r = row.original
          // Tooltip: reason + who credited + when (mirrors deductible tooltip)
          const reason = r.correction_reason || r.correction_comments || ''
          const when = r.credited_at ? new Date(r.credited_at).toLocaleString('fi-FI') : ''
          const who = r.credited_by_name || ''
          const meta = (when || who)
            ? `${t('materials.creditedInfo')}: ${when}${who ? ' — ' + who : ''}`
            : ''
          const tip = [reason, meta].filter(Boolean).join('\n') || undefined
          if (r.entry_type === 'credit')
            return <span title={tip} className="text-red-600 font-semibold text-xs">{t('materials.credit')}</span>
          if (r.entry_type === 'correction')
            return <span title={tip} className="text-blue-600 font-semibold text-xs">{t('materials.correction')}</span>
          if (r.entry_type === 'original_correction')
            return <span title={tip} className="text-green-600 font-semibold text-xs">{t('materials.originalCorrection')}</span>
          if (r.is_credited)
            return <span title={tip} className="text-gray-400 text-xs line-through">{t('materials.credited')}</span>
          if ((r as MaterialRow & { is_corrected?: boolean }).is_corrected)
            return <span title={tip} className="text-gray-400 text-xs line-through">{t('materials.corrected')}</span>
          return null
        },
        filterFn: 'includesString',
        size: 70, minSize: 50,
      },
      // Vehicle identifiers at accounting (bill) level — last columns.
      {
        id: 'vehicle_car_id',
        accessorKey: 'vehicle_car_id',
        header: t('inspection.vehicleCarId'),
        filterFn: 'includesString',
        size: 90, minSize: 50,
      },
      {
        id: 'vehicle_reg_number',
        accessorKey: 'vehicle_reg_number',
        header: t('inspection.vehicleReg'),
        filterFn: 'includesString',
        size: 90, minSize: 50,
      },
    ],
    [t]
  )

  // Define sub-row (inspection) column definitions
  const subColumns = useMemo(
    () => [
      {
        key: 'booking_datetime',
        header: t('inspection.bookingDatetime'),
        width: '55px',
        render: (val: unknown) => formatTime(val as string | null),
      },
      {
        key: 'booking_uuid',
        header: t('inspection.bookingUuid'),
        width: '95px',
        render: (val: unknown) => truncUuid(val),
      },
      {
        key: 'customer_share',
        header: t('inspection.customerShare'),
        width: '85px',
        align: 'right' as const,
        render: (val: unknown) => formatCurrency(val as string | null),
      },
      {
        key: 'user_uuid',
        header: t('inspection.customer'),
        width: '150px',
        render: (val: unknown) => truncUuid(val),
      },
      {
        key: 'account_type_name',
        header: t('inspection.accountType'),
        width: '220px',
      },
      {
        key: 'name_last',
        header: t('inspection.lastName'),
        width: '100px',
      },
      {
        key: 'name_first',
        header: t('inspection.firstName'),
        width: '100px',
      },
      {
        key: 'address',
        header: t('inspection.address'),
        width: '550px',
        truncate: true,
      },
      {
        key: 'deductible_invoiced',
        header: t('inspection.deductibleInvoiced'),
        width: '35px',
        render: (val: unknown) => (val ? '\u2713' : ''),
      },
      {
        key: 'copayment',
        header: t('inspection.copayment'),
        width: '80px',
        align: 'right' as const,
        render: (val: unknown, row: InspectionRow) => {
          const corrs = row.deductible_corrections || []
          if (corrs.length > 0) {
            // Prefer the last correction (newest) for the displayed value —
            // for a credit+correction pair this is the correction row that
            // sets the new net amount.
            const lead = corrs[corrs.length - 1]
            const color = lead.correction_type === 'credit' ? 'text-red-600'
              : lead.correction_type === 'correction' ? 'text-blue-600'
              : 'text-orange-600'
            const tooltip = corrs
              .map(c => `${t('deductibleCorr.original')}: ${formatCurrency(c.original_value)} → ${formatCurrency(c.corrected_value)} (${c.correction_type}) | ${c.created_by_name} ${c.created_at ? new Date(c.created_at).toLocaleDateString('fi-FI') : ''}`)
              .join('\n')
            return (
              <span className={`${color} font-semibold`} title={tooltip}>
                ⚠ {formatCurrency(lead.corrected_value)}
              </span>
            )
          }
          return formatCurrency(val as string | null)
        },
      },
      {
        key: 'distance_meters',
        header: t('inspection.distance'),
        width: '80px',
        align: 'right' as const,
        render: (val: unknown) => formatKm(val),
      },
      {
        key: 'distance_percentage',
        header: t('inspection.distancePercentage'),
        width: '70px',
        align: 'right' as const,
        render: (val: unknown) => formatPct(val),
      },
      // Route total (sum of all item amounts)
      {
        key: 'route_total',
        header: t('inspection.routeTotal'),
        width: '70px',
        align: 'right' as const,
        render: (_val: unknown, row: InspectionRow) => {
          const total = (row.items || []).reduce(
            (s, i) => s + Number(i.amount_net || 0),
            0
          )
          return total ? formatCurrency(String(total)) : ''
        },
      },
      {
        key: 'is_combination',
        header: t('inspection.combination'),
        width: '35px',
        render: (val: unknown) => (val ? 'K' : ''),
      },
      // Pivot columns per item type
      ...ITEM_TYPES.map((it) => ({
        key: `item_${it.id}`,
        header: t(`inspection.items.${it.tKey}`),
        width: '65px',
        align: 'right' as const,
        render: (_val: unknown, row: InspectionRow) => {
          const item = row.items?.find((i) => i.type_id === it.id)
          return item ? formatCurrency(item.amount_net) : ''
        },
      })),
      // Combined: Customer_Copayment (7) + Customer_Direct_Payment (10)
      {
        key: 'item_extra_passenger',
        header: t('inspection.items.extraPassenger'),
        width: '65px',
        align: 'right' as const,
        render: (_val: unknown, row: InspectionRow) => {
          const a7 = row.items?.find((i) => i.type_id === 7)
          const a10 = row.items?.find((i) => i.type_id === 10)
          const total =
            Number(a7?.amount_net || 0) + Number(a10?.amount_net || 0)
          return total ? formatCurrency(String(total)) : ''
        },
      },
      {
        key: 'municipality_name',
        header: t('accounting.municipality'),
        width: '100px',
      },
      {
        key: 'additional_info',
        header: t('inspection.additionalInfo'),
        width: '350px',
        truncate: true,
      },
      {
        key: 'correction_comments',
        header: t('materials.correctionCommentsShort'),
        width: '350px',
        render: (val: unknown) => {
          if (!val) return ''
          return String(val)
        },
        truncate: true,
      },
    ],
    [t]
  )

  // Cast the data for TanStack Table (parent + child go through same row model)
  const tableData = useMemo<MaterialRow[]>(() => {
    if (!data?.data) return []
    return data.data as MaterialRow[]
  }, [data])

  // Column header map for filter info display in Excel
  const columnHeaderMap: Record<string, string> = useMemo(() => ({
    bill_datetime: t('accounting.billDate'),
    bill_uuid: t('accounting.billUuid'),
    amount_net: t('accounting.amountNet'),
    cost_center: t('accounting.costCenter'),
    cost_center_description: t('accounting.description'),
    sub_org_prefix: t('accounting.subOrgUuid'),
    billing_group: t('accounting.billingGroup'),
    bill_status: t('accounting.status'),
    invoice_ref: t('materials.invoice'),
    invoice_line_number: t('materials.lineNumber'),
  }), [t])

  const handleExport = useCallback(
    (
      filteredParents: MaterialRow[],
      _filteredChildren: MaterialRow[],
      filterInfo?: { globalFilter: string; columnFilters: ColumnFiltersState },
    ) => {
      if (!filteredParents.length) return

      // -- Styles --
      const metaStyle = { font: { bold: true, sz: 11 } }
      const filterStyle = { font: { italic: true, sz: 10, color: { rgb: '666666' } } }
      const headerStyle = {
        font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '4472C4' } },
        border: { bottom: { style: 'thin', color: { rgb: '2F5496' } } },
      }
      const evenRowStyle = { fill: { fgColor: { rgb: 'F2F2F2' } } }
      const moneyFmt = '#,##0.00'

      // -- Column definitions (flat: parent + child merged) --
      interface FlatCol {
        header: string
        width: number
        getValue: (p: AccountingRow, c: InspectionRow, idx: number) => unknown
        isMoney?: boolean
      }

      const flatCols: FlatCol[] = [
        // Parent fields
        { header: t('accounting.billDate'), width: 12,
          getValue: (p) => p.bill_datetime ? new Date(p.bill_datetime).toLocaleDateString('fi-FI') : '' },
        { header: t('accounting.billUuid'), width: 16,
          getValue: (p) => p.bill_uuid || '' },
        { header: t('accounting.amountNet'), width: 14, isMoney: true,
          getValue: (p) => p.amount_net ? Number(p.amount_net) : '' },
        { header: t('accounting.costCenter'), width: 14,
          getValue: (p) => p.cost_center || '' },
        { header: t('accounting.description'), width: 24,
          getValue: (p) => p.cost_center_description || '' },
        { header: t('accounting.subOrgUuid'), width: 8,
          getValue: (p) => p.sub_org_prefix || '' },
        { header: t('accounting.billingGroup'), width: 24,
          getValue: (p) => p.billing_group || '' },
        { header: t('accounting.status'), width: 6,
          getValue: (p) => p.is_rejected ? 'H' : p.is_blocked ? 'E' : '' },
        { header: t('materials.invoice'), width: 12,
          getValue: (p) => p.invoice_access_code || '' },
        { header: t('materials.lineNumber'), width: 6,
          getValue: (p) => p.invoice_line_number != null ? p.invoice_line_number : '' },
        { header: t('materials.entryType'), width: 10,
          getValue: (p) => {
            if (p.entry_type === 'credit') return t('materials.credit')
            if (p.entry_type === 'correction') return t('materials.correction')
            if (p.is_credited) return t('materials.credited')
            return ''
          } },
        // Sub-row index
        { header: '#', width: 4,
          getValue: (_p, _c, idx) => idx },
        // Child fields
        { header: t('inspection.bookingDatetime'), width: 14,
          getValue: (_p, c) => c.booking_datetime ? new Date(c.booking_datetime).toLocaleString('fi-FI', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '' },
        { header: t('inspection.bookingUuid'), width: 16,
          getValue: (_p, c) => c.booking_uuid || '' },
        { header: t('inspection.lastName'), width: 14,
          getValue: (_p, c) => c.name_last || '' },
        { header: t('inspection.firstName'), width: 14,
          getValue: (_p, c) => c.name_first || '' },
        { header: t('inspection.address'), width: 28,
          getValue: (_p, c) => c.address || '' },
        { header: t('accounting.municipality'), width: 14,
          getValue: (_p, c) => c?.municipality_name || '' },
        { header: t('inspection.customerShare'), width: 12, isMoney: true,
          getValue: (_p, c) => c.customer_share ? Number(c.customer_share) : '' },
        { header: t('inspection.copayment'), width: 12, isMoney: true,
          getValue: (_p, c) => {
            const corrs = c.deductible_corrections || []
            if (corrs.length > 0) return Number(corrs[corrs.length - 1].corrected_value)
            return c.copayment ? Number(c.copayment) : ''
          } },
        { header: t('inspection.accountType'), width: 16,
          getValue: (_p, c) => c.account_type_name || '' },
        { header: t('inspection.costCenter'), width: 14,
          getValue: (_p, c) => c.cost_center || '' },
        { header: t('inspection.vehicleCarId'), width: 10,
          getValue: (_p, c) => c.vehicle_car_id || '' },
        { header: t('inspection.vehicleReg'), width: 10,
          getValue: (_p, c) => c.vehicle_reg_number || '' },
        { header: t('inspection.distance') + ' km', width: 10,
          getValue: (_p, c) => c.distance_meters != null ? Math.round(c.distance_meters / 100) / 10 : '' },
        // Item type pivot columns
        ...ITEM_TYPES.map((it) => ({
          header: t(`inspection.items.${it.tKey}`),
          width: 10,
          isMoney: true,
          getValue: (_p: AccountingRow, c: InspectionRow) => {
            const item = c.items?.find((i) => i.type_id === it.id)
            return item ? Number(item.amount_net) : ''
          },
        })),
        { header: t('inspection.items.extraPassenger'), width: 12, isMoney: true,
          getValue: (_p: AccountingRow, c: InspectionRow) => {
            const a7 = c.items?.find((i) => i.type_id === 7)
            const a10 = c.items?.find((i) => i.type_id === 10)
            const total = Number(a7?.amount_net || 0) + Number(a10?.amount_net || 0)
            return total ? total : ''
          },
        },
        { header: t('inspection.combination'), width: 6,
          getValue: (_p, c) => c.is_combination ? 'K' : '' },
        { header: t('inspection.additionalInfo'), width: 20,
          getValue: (_p, c) => c.additional_info || '' },
        { header: t('materials.correctionCommentsShort'), width: 20,
          getValue: (_p, c) => c.correction_comments || '' },
      ]

      // -- Build meta rows --
      const colCount = flatCols.length
      const rows: unknown[][] = []

      // Row 0: title with export batch info
      const batchLabel = billExportUuid === '_open'
        ? `${t('materials.title')} — ${t('bills.open')}`
        : `${t('materials.title')} — ${t('bills.exportBatch')}: ${billExportUuid || ''}`
      rows.push([batchLabel])

      // Compute date range from actual data
      const allDates = filteredParents
        .map((p) => p.bill_datetime)
        .filter((d): d is string => !!d)
        .sort()
      const dateRange = allDates.length > 0
        ? `${new Date(allDates[0]).toLocaleDateString('fi-FI')} – ${new Date(allDates[allDates.length - 1]).toLocaleDateString('fi-FI')}`
        : ''
      const statsLine = [
        dateRange ? `${t('bills.dateFrom')}: ${dateRange}` : '',
        `${filteredParents.length} ${t('materials.accountingRows')}`,
      ].filter(Boolean).join(' | ')
      rows.push([statsLine])

      // Row 2: filter info (if any)
      const filterParts: string[] = []
      if (filterInfo?.globalFilter) {
        filterParts.push(`Haku: "${filterInfo.globalFilter}"`)
      }
      if (filterInfo?.columnFilters?.length) {
        for (const cf of filterInfo.columnFilters) {
          const label = columnHeaderMap[cf.id] || cf.id
          filterParts.push(`${label}: "${cf.value}"`)
        }
      }
      if (filterParts.length > 0) {
        rows.push([`Suodattimet: ${filterParts.join(', ')}`])
      } else {
        rows.push([])
      }

      // Row 3: empty separator
      rows.push([])

      // Header row index
      const headerRowIdx = rows.length

      // -- Column headers --
      const headerRow = flatCols.map((c) => c.header)
      rows.push(headerRow)

      // -- Build flat data rows --
      const dataRows: unknown[][] = []

      for (const parent of filteredParents) {
        const acc = parent as AccountingRow
        const children = acc.subRows || []
        if (children.length === 0) {
          const emptyChild = {} as InspectionRow
          dataRows.push(flatCols.map((col) => col.getValue(acc, emptyChild, 0)))
        } else {
          for (let i = 0; i < children.length; i++) {
            dataRows.push(flatCols.map((col) => col.getValue(acc, children[i], i)))
          }
        }
      }
      rows.push(...dataRows)

      const ws = XLSX.utils.aoa_to_sheet(rows)

      // -- Apply styles --
      // Meta rows (0-1): bold
      for (let r = 0; r < 2; r++) {
        const addr = XLSX.utils.encode_cell({ r, c: 0 })
        if (ws[addr]) ws[addr].s = metaStyle
      }
      // Filter row (2): italic gray
      if (filterParts.length > 0) {
        const addr = XLSX.utils.encode_cell({ r: 2, c: 0 })
        if (ws[addr]) ws[addr].s = filterStyle
      }

      // Header row: bold white on blue
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r: headerRowIdx, c })
        if (ws[addr]) ws[addr].s = headerStyle
      }

      // Data rows: alternating gray, money format
      for (let i = 0; i < dataRows.length; i++) {
        const rowIdx = headerRowIdx + 1 + i
        for (let c = 0; c < colCount; c++) {
          const addr = XLSX.utils.encode_cell({ r: rowIdx, c })
          const isEven = i % 2 === 0
          const col = flatCols[c]
          if (ws[addr]) {
            const s: Record<string, unknown> = {}
            if (isEven) Object.assign(s, evenRowStyle)
            if (col.isMoney && typeof ws[addr].v === 'number') {
              s.numFmt = moneyFmt
            }
            if (Object.keys(s).length > 0) ws[addr].s = s
          } else if (isEven) {
            ws[addr] = { v: '', t: 's', s: evenRowStyle }
          }
        }
      }

      // Column widths
      ws['!cols'] = flatCols.map((col) => ({ wch: col.width }))

      // AutoFilter on header row
      ws['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: headerRowIdx, c: 0 },
          e: { r: headerRowIdx + dataRows.length, c: colCount - 1 },
        }),
      }

      // Freeze panes: header row stays visible when scrolling
      ws['!freeze'] = { xSplit: 0, ySplit: headerRowIdx + 1 }

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Aineisto')
      const today = new Date().toISOString().slice(0, 10)
      XLSX.writeFile(wb, `aineisto_${today}.xlsx`)
    },
    [t, billExportUuid, columnHeaderMap]
  )

  const renderToolbarExtra = useCallback(
    (
      filteredParents: MaterialRow[],
      filteredChildren: MaterialRow[],
      filterInfo?: { globalFilter: string; columnFilters: ColumnFiltersState },
    ) => (
      <button
        onClick={() => handleExport(filteredParents, filteredChildren, filterInfo)}
        disabled={!filteredParents.length}
        className="flex items-center gap-1 px-2 py-1 rounded border hover:bg-green-50 text-green-700 border-green-200 disabled:opacity-50"
      >
        <Download className="w-3 h-3" />
        {t('actions.export')}
      </button>
    ),
    [t, handleExport]
  )

  const renderExtraStats = useCallback(
    (parents: MaterialRow[], children: MaterialRow[]) => {
      const bills = new Set<string>()
      const bookings = new Set<string>()
      const users = new Set<string>()
      const creditedBills = new Set<string>()
      const correctedBills = new Set<string>()
      for (const p of parents) {
        if (p.bill_uuid) bills.add(p.bill_uuid)
        if (p.entry_type === 'credit' && p.bill_uuid) creditedBills.add(p.bill_uuid)
        if (p.entry_type === 'correction' && p.bill_uuid) correctedBills.add(p.bill_uuid)
      }
      for (const c of children) {
        if (c.booking_uuid) bookings.add(c.booking_uuid)
        if (c.user_uuid) users.add(c.user_uuid)
      }
      return (
        <>
          <span className="border-l pl-3">{bills.size} {t('materials.uniqueBills')}</span>
          <span>{bookings.size} {t('materials.uniqueBookings')}</span>
          <span>{users.size} {t('materials.uniqueUsers')}</span>
          {creditedBills.size > 0 && (
            <span className="border-l pl-3 text-red-600">{creditedBills.size} {t('materials.creditedBills')}</span>
          )}
          {correctedBills.size > 0 && (
            <span className="text-blue-600">{correctedBills.size} {t('materials.correctedBills')}</span>
          )}
        </>
      )
    },
    [t]
  )

  if (isLoading) return <LoadingSpinner />

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{tc('errors.generic')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4 shrink-0">
        <Link
          to="/payment-traffic"
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {t('materials.title')}
            {billExportUuid && billExportUuid !== '_open' && (
              <span className="ml-2 text-sm font-mono text-gray-500">
                {billExportUuid.slice(0, 8)}..{billExportUuid.slice(-4)}
              </span>
            )}
            {billExportUuid === '_open' && (
              <span className="ml-2 text-sm text-yellow-600">
                ({t('bills.open')})
              </span>
            )}
          </h1>
        </div>
      </div>

      {/* Filter help tooltip */}
      <div className="flex items-center gap-1.5 mb-2 text-xs text-gray-500 shrink-0">
        <HelpCircle className="w-3.5 h-3.5" />
        <span
          className="cursor-help underline decoration-dotted"
          title={t('materials.filterHelpContent')}
        >
          {t('materials.filterHelp')}
        </span>
      </div>

      {/* Table */}
      <VirtualTable<MaterialRow, InspectionRow>
        data={tableData}
        columns={columns}
        getSubRows={(row) => row.subRows as unknown as MaterialRow[] | undefined}
        subColumns={subColumns}
        asChild={(row) => row as unknown as InspectionRow}
        estimateSize={28}
        overscan={30}
        filterPlaceholder={t('table.filterPlaceholder')}
        globalFilterPlaceholder={t('table.globalSearch')}
        expandAllLabel={t('materials.expandAll')}
        collapseAllLabel={t('materials.collapseAll')}
        rowCountLabel={t('materials.accountingRows')}
        subRowCountLabel={t('materials.inspectionRows')}
        copyLabel={tc('common.copy')}
        clearFiltersLabel={t('table.clearFilters')}
        renderExtraStats={renderExtraStats}
        renderToolbarExtra={renderToolbarExtra}
        getRowClassName={(row: MaterialRow) => {
          if (row.entry_type === 'credit') return '!bg-red-50'
          if (row.entry_type === 'correction') return '!bg-blue-50'
          if (row.entry_type === 'original_correction') return '!bg-green-50'
          if (row.is_credited) return '!bg-gray-100 !line-through'
          if ((row as MaterialRow & { is_corrected?: boolean }).is_corrected) return '!bg-gray-100 !line-through'
          if (row.is_rejected) return '!bg-red-100 !text-gray-400 !line-through'
          if (row.is_blocked) return '!bg-red-50 !text-gray-400'
          if (row.is_non_billable) return '!bg-blue-50 !text-gray-400'
          if (row.invoice_status === 'published') return '!bg-green-50'
          if (row.invoice_line_id) return '!bg-yellow-50'
          return ''
        }}
        contextMenuItems={(row: MaterialRow) => {
          if (!row.bill_uuid) return []
          const items: { label: string; icon?: React.ReactNode; onClick: () => void }[] = []
          // Block/unblock only if not yet invoiced and not rejected.
          // Blocking is independent of non-billable: a blocked bill is paused
          // for both invoicing AND copayment processing until unblocked.
          if (!row.invoice_line_id && !row.is_rejected) {
            items.push({
              label: row.is_blocked ? t('materials.unblock') : t('materials.block'),
              icon: row.is_blocked
                ? <ShieldCheck className="w-3 h-3" />
                : <ShieldBan className="w-3 h-3" />,
              onClick: () => blockMutation.mutate({
                billUuid: row.bill_uuid!,
                isBlocked: !row.is_blocked,
              }),
            })
          }
          // Mark/unmark as non-billable (day car): excluded from invoicing,
          // copayments still processed. Only if not yet invoiced and not rejected.
          if (!row.invoice_line_id && !row.is_rejected) {
            items.push({
              label: row.is_non_billable ? t('materials.unmarkNonBillable') : t('materials.markNonBillable'),
              icon: row.is_non_billable
                ? <ShieldCheck className="w-3 h-3" />
                : <CircleSlash className="w-3 h-3" />,
              onClick: () => nonBillableMutation.mutate({
                billUuid: row.bill_uuid!,
                isNonBillable: !row.is_non_billable,
              }),
            })
          }
          // Reject/unreject: mark as permanently excluded
          if (!row.invoice_line_id && !row.is_rejected) {
            items.push({
              label: t('accounting.reject'),
              icon: <Ban className="w-3 h-3" />,
              onClick: () => rejectMutation.mutate({
                billUuid: row.bill_uuid!,
                isRejected: true,
              }),
            })
          }
          if (row.is_rejected) {
            items.push({
              label: t('accounting.undoReject'),
              icon: <Undo2 className="w-3 h-3" />,
              onClick: () => rejectMutation.mutate({
                billUuid: row.bill_uuid!,
                isRejected: false,
              }),
            })
          }
          if (row.invoice_access_code && row.invoice_status === 'published') {
            items.push({
              label: t('materials.openInvoice'),
              icon: <ExternalLink className="w-3 h-3" />,
              onClick: () => window.open(`/invoices/access/${row.invoice_access_code}`, '_blank'),
            })
          }
          // Credit/Correction: for invoiced, non-credited entries. Includes
          // already-created correction rows that were themselves invoiced, so a
          // corrected+invoiced trip can be credited and re-corrected (e.g. to add
          // a missing surcharge). 'credit' rows are never creditable.
          if (
            row.invoice_line_id &&
            !row.is_credited &&
            ['original', 'correction', 'original_correction'].includes(row.entry_type)
          ) {
            items.push({
              label: t('materials.creditCorrect'),
              icon: <RotateCcw className="w-3 h-3" />,
              onClick: () => { setWizardBillUuid(row.bill_uuid); setWizardPreInvoice(false) },
            })
          }
          // Pre-invoice correction: for non-invoiced, non-corrected originals
          if (
            !row.invoice_line_id &&
            !(row as MaterialRow & { is_corrected?: boolean }).is_corrected &&
            row.entry_type === 'original' &&
            !row.is_blocked
          ) {
            items.push({
              label: t('materials.preInvoiceCorrect'),
              icon: <Pencil className="w-3 h-3" />,
              onClick: () => { setWizardBillUuid(row.bill_uuid); setWizardPreInvoice(true) },
            })
          }
          // Delete correction batch: for uninvoiced credit/correction/original_correction entries
          if (
            row.entry_type &&
            ['credit', 'correction', 'original_correction'].includes(row.entry_type) &&
            !row.invoice_line_id &&
            row.correction_batch_id
          ) {
            items.push({
              label: t('materials.deleteCorrection'),
              icon: <Trash2 className="w-3 h-3" />,
              onClick: () => openDeleteConfirm(row.correction_batch_id!),
            })
          }
          return items
        }}
        subContextMenuItems={(row: InspectionRow) => {
          const items: { label: string; icon?: React.ReactNode; onClick: () => void }[] = []
          const corrs = row.deductible_corrections || []
          // Pending = not yet materialised/sent to the wellbeing area; these
          // can still be undone. Once sent (is_picked_up), a correction is
          // locked and can only be superseded via "re-correct".
          const pending = corrs.filter(c => !c.is_picked_up)
          if (corrs.length === 0) {
            // Not corrected — offer zero / first correction
            const cop = parseFloat(row.copayment || '0')
            if (cop > 0) {
              items.push({
                label: t('deductibleCorr.zero'),
                icon: <ZapOff className="w-3 h-3" />,
                onClick: () => deductibleZeroMutation.mutate(row.id),
              })
            }
            items.push({
              label: t('deductibleCorr.correct'),
              icon: <Pencil className="w-3 h-3" />,
              onClick: () => setDeductibleCorrModal(row),
            })
          } else if (pending.length > 0) {
            // Latest correction is NOT yet sent → allow undoing only it. The
            // backend reverts the whole pending re-correction atomically
            // (correction then credit), so show ONE undo based on the value-
            // bearing entry. Older/sent corrections and re-correct are hidden —
            // no editing a middle entry once a newer correction exists.
            const valueCorr = pending.find(c => c.correction_type !== 'credit') || pending[0]
            if (valueCorr.correction_id) {
              items.push({
                label: `${t('deductibleCorr.undo')}: ${formatCurrency(valueCorr.original_value)} → ${formatCurrency(valueCorr.corrected_value)}`,
                icon: <Undo2 className="w-3 h-3" />,
                onClick: () => deductibleUndoMutation.mutate(valueCorr.correction_id),
              })
            }
          } else {
            // All corrections already sent to the wellbeing area → only allow
            // superseding via a new correction (credit current + charge new),
            // or zeroing the current value.
            const latest = corrs[corrs.length - 1]
            if (parseFloat(latest.corrected_value || '0') > 0) {
              items.push({
                label: t('deductibleCorr.zero'),
                icon: <ZapOff className="w-3 h-3" />,
                onClick: () => deductibleZeroMutation.mutate(row.id),
              })
            }
            items.push({
              label: t('deductibleCorr.recorrect'),
              icon: <Pencil className="w-3 h-3" />,
              onClick: () => setDeductibleCorrModal(row),
            })
          }
          // Copayment history (billing, corrections, invoice credits — who/when)
          if (corrs.length > 0 || row.deductible_invoiced) {
            items.push({
              label: t('deductibleCorr.history'),
              icon: <History className="w-3 h-3" />,
              onClick: () => setHistoryRow(row),
            })
          }
          return items
        }}
      />

      {historyRow && (
        <DeductibleHistoryModal
          source={{ inspectionEntryId: historyRow.id }}
          subtitle={`${historyRow.name_last ?? ''} ${historyRow.name_first ?? ''}${
            historyRow.booking_datetime
              ? ' — ' + new Date(historyRow.booking_datetime).toLocaleDateString('fi-FI')
              : ''
          }`}
          onClose={() => setHistoryRow(null)}
        />
      )}

      {/* Deductible correction success message */}
      {deductibleSuccessMsg && (
        <div className="fixed top-4 right-4 z-50 bg-green-100 text-green-800 px-4 py-2 rounded shadow-lg text-sm">
          {deductibleSuccessMsg}
        </div>
      )}

      {mutationError && (
        <div className="fixed top-4 right-4 z-50 bg-red-100 text-red-800 px-4 py-2 rounded shadow-lg text-sm max-w-md">
          {mutationError}
        </div>
      )}

      {/* Deductible correction modal */}
      {deductibleCorrModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[400px]">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold">{t('deductibleCorr.correctTitle')}</h3>
              <button onClick={() => setDeductibleCorrModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <p className="text-sm text-gray-600">
                {deductibleCorrModal.name_last} {deductibleCorrModal.name_first}
                {deductibleCorrModal.booking_datetime && (
                  <span className="ml-1 text-gray-400">
                    — {new Date(deductibleCorrModal.booking_datetime).toLocaleDateString('fi-FI')}
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span>{t('deductibleCorr.currentValue')}:</span>
                <span className="font-mono font-semibold">{formatCurrency(
                  // Show the CURRENT effective value: the latest correction's
                  // value if already corrected, otherwise the original copayment.
                  deductibleCorrModal.deductible_corrections && deductibleCorrModal.deductible_corrections.length > 0
                    ? deductibleCorrModal.deductible_corrections[deductibleCorrModal.deductible_corrections.length - 1].corrected_value
                    : deductibleCorrModal.copayment
                )}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label>{t('deductibleCorr.newValue')}:</label>
                <input
                  id="deductible-corr-value"
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-28 border rounded px-2 py-1 text-right font-mono text-sm"
                  defaultValue=""
                  autoFocus
                />
                <span>&euro;</span>
              </div>
              {(!deductibleCorrModal.deductible_corrections || deductibleCorrModal.deductible_corrections.length === 0) && deductibleCorrModal.deductible_invoiced && (
                <p className="text-xs text-amber-600">
                  {t('deductibleCorr.approvedNote')}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t bg-gray-50">
              <button className="px-3 py-1.5 text-sm text-gray-600" onClick={() => setDeductibleCorrModal(null)}>
                {t('correction.previous')}
              </button>
              <button
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                disabled={deductibleCorrectMutation.isPending}
                onClick={() => {
                  const input = document.getElementById('deductible-corr-value') as HTMLInputElement
                  const val = parseFloat(input?.value || '0')
                  if (val >= 0) {
                    deductibleCorrectMutation.mutate({ inspId: deductibleCorrModal.id, value: val })
                  }
                }}
              >
                {t('correction.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {wizardBillUuid && (
        <CorrectionWizard
          billUuid={wizardBillUuid}
          preInvoice={wizardPreInvoice}
          onClose={() => setWizardBillUuid(null)}
          onSuccess={() => {
            setWizardBillUuid(null)
            // Optimistic: mark rows immediately
            queryClient.setQueryData<MaterialsResponse>(['billingMaterials', exportUuidParam], (old) => {
              if (!old) return old
              return {
                ...old,
                data: old.data.map(ae =>
                  ae.bill_uuid === wizardBillUuid && ae.entry_type === 'original'
                    ? wizardPreInvoice
                      ? { ...ae, is_corrected: true }
                      : ae.invoice_line_id
                        ? { ...ae, is_credited: true }
                        : ae
                    : ae
                ),
              }
            })
            queryClient.invalidateQueries({ queryKey: ['billingMaterials'] })
          }}
        />
      )}

      {/* Delete correction batch confirmation modal */}
      {deleteBatchId && deleteSummary && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[500px] max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">{t('materials.deleteCorrectionTitle')}</h2>
              <button onClick={() => { setDeleteBatchId(null); setDeleteSummary(null) }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              <p className="text-sm text-gray-600">{t('materials.deleteCorrectionDesc')}</p>
              <div className="border rounded divide-y text-sm">
                {deleteSummary.entries.map((e, i) => (
                  <div key={i} className={`flex justify-between px-3 py-2 ${e.entry_type === 'credit' ? 'bg-red-50' : 'bg-blue-50'}`}>
                    <span>
                      <span className={`font-semibold text-xs mr-1 ${e.entry_type === 'credit' ? 'text-red-600' : 'text-blue-600'}`}>
                        {e.entry_type === 'credit' ? t('materials.credit') : t('materials.correction')}
                      </span>
                      {e.cost_center} {e.cost_center_description}
                    </span>
                    <span className="font-mono">{formatCurrency(e.amount_net)} &euro;</span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500">
                {t('materials.deleteCorrectionSummary', {
                  credits: deleteSummary.credit_count,
                  corrections: deleteSummary.correction_count,
                  inspections: deleteSummary.inspection_count,
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
              <button
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                onClick={() => { setDeleteBatchId(null); setDeleteSummary(null) }}
              >
                {t('correction.previous')}
              </button>
              <button
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                disabled={deleteCorrectionMutation.isPending}
                onClick={() => deleteCorrectionMutation.mutate(deleteBatchId)}
              >
                {t('materials.deleteCorrectionConfirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
